import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, ArrowRight, FileText, Shield, Copy, Scale, Workflow, BarChart3, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfa, getMfaSetup } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaEnforced, setMfaEnforced] = useState(false);
  const idleSignOut = new URLSearchParams(location.search).get("reason") === "idle";
  
  const [formData, setFormData] = useState({ email: "", password: "" });

  const from = location.state?.from?.pathname || "/dashboard";

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success("Welcome back!");
        navigate(from, { replace: true });
      } else if (result.mfa_required) {
        setMfaToken(result.mfa_token);
        setMfaEnforced(!!result.mfa_enforced);
        if (!result.mfa_setup_complete) {
          const setup = await getMfaSetup(result.mfa_token);
          if (setup.success) setMfaSetupData(setup);
        }
        setMfaStep(true);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    if (mfaCode.length < 6) { toast.error("Enter a 6-digit code"); return; }
    setLoading(true);
    try {
      const result = await verifyMfa(mfaToken, mfaCode);
      if (result.success) {
        toast.success("Welcome back! MFA verified.");
        navigate(from, { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80)` }} />
        <div className="absolute inset-0 bg-[#0a1628]/85" />

        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full">
          <div className="shrink-0">
            <h2 className="text-4xl xl:text-5xl font-extrabold tracking-tight">
              <span className="text-white">Con</span><span className="text-[#22d3ee]">Track</span>
            </h2>
            <p className="text-[#94a3b8] text-xs tracking-[0.25em] uppercase font-medium mt-2">Contract Lifecycle Management</p>
          </div>

          <div className="flex-1 flex flex-col justify-center py-10">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Streamline your contract<br />management <span className="text-[#22d3ee]">workflow</span>
            </h1>
            <p className="text-[#94a3b8] text-sm leading-relaxed max-w-md mb-10">
              Draft, review, approve, and manage contracts with AI-powered intelligence — turning complex processes into streamlined workflows.
            </p>

            <div className="grid grid-cols-2 gap-3 max-w-md mb-10">
              <div className="bg-white/[0.06] backdrop-blur-md border border-white/[0.08] rounded-lg p-4 transition-colors hover:bg-white/[0.1]">
                <FileText size={20} className="text-[#22d3ee] mb-2.5" />
                <p className="text-white text-sm font-semibold mb-0.5">Smart Drafting</p>
                <p className="text-[#64748b] text-xs">AI-powered contract creation</p>
              </div>
              <div className="bg-white/[0.06] backdrop-blur-md border border-white/[0.08] rounded-lg p-4 transition-colors hover:bg-white/[0.1]">
                <Workflow size={20} className="text-[#22d3ee] mb-2.5" />
                <p className="text-white text-sm font-semibold mb-0.5">Automated Workflows</p>
                <p className="text-[#64748b] text-xs">Approval processes on autopilot</p>
              </div>
              <div className="bg-white/[0.06] backdrop-blur-md border border-white/[0.08] rounded-lg p-4 transition-colors hover:bg-white/[0.1]">
                <Scale size={20} className="text-[#22d3ee] mb-2.5" />
                <p className="text-white text-sm font-semibold mb-0.5">Risk Management</p>
                <p className="text-[#64748b] text-xs">AI clause &amp; obligation review</p>
              </div>
              <div className="bg-white/[0.06] backdrop-blur-md border border-white/[0.08] rounded-lg p-4 transition-colors hover:bg-white/[0.1]">
                <BarChart3 size={20} className="text-[#22d3ee] mb-2.5" />
                <p className="text-white text-sm font-semibold mb-0.5">Compliance Tracking</p>
                <p className="text-[#64748b] text-xs">Full lifecycle monitoring</p>
              </div>
            </div>

            <div className="flex gap-8 max-w-md">
              <div>
                <p className="text-[#22d3ee] text-xl font-bold">60%</p>
                <p className="text-[#64748b] text-xs mt-0.5">Faster Drafting</p>
              </div>
              <div>
                <p className="text-[#22d3ee] text-xl font-bold">500+</p>
                <p className="text-[#64748b] text-xs mt-0.5">Contracts Managed</p>
              </div>
              <div>
                <p className="text-[#22d3ee] text-xl font-bold">99%</p>
                <p className="text-[#64748b] text-xs mt-0.5">Compliance Rate</p>
              </div>
            </div>
          </div>

          <p className="text-[#475569] text-xs">&copy; 2026 ConTrack by ProcureAide. All rights reserved.</p>
        </div>
      </div>

      {/* Right Panel — structure matches DataRefinery exactly */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen bg-white">
        <div className="lg:hidden p-6 pb-0">
          <h2 className="text-2xl font-extrabold tracking-tight">
            <span className="text-[#0f172a]">Con</span><span className="text-[#0891b2]">Track</span>
          </h2>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-12 lg:px-16 xl:px-24">
          <div className="w-full max-w-[420px]">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a] mb-1" data-testid="login-heading">
                {mfaStep ? "Verify your identity" : forgotMode ? "Reset password" : "Welcome back"}
              </h1>
              <p className="text-[#64748b] text-sm mb-8">
                {mfaStep
                  ? "Enter the 6-digit code from your authenticator app"
                  : forgotMode
                    ? "Enter your email and we\u2019ll send you a reset link"
                    : <>Sign in to continue to <span className="font-semibold text-[#0f172a]">ConTrack</span></>
                }
              </p>

              {idleSignOut && !mfaStep && (
                <p className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-md p-3 mb-5" data-testid="idle-signout-notice">
                  You were signed out after 30 minutes of inactivity. Please sign in again.
                </p>
              )}
              {mfaStep ? (
                <form onSubmit={handleMfaVerify} className="space-y-5">
                  {mfaEnforced && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="mfa-enforced-notice">
                      Your organisation requires two-step verification. Set it up now to continue.
                    </p>
                  )}
                  {mfaSetupData && (
                    <div className="space-y-3 p-4 border border-cyan-200 bg-cyan-50/30 rounded-md">
                      <div className="flex items-center gap-2 text-sm font-medium text-cyan-800">
                        <Shield size={16} />First-time setup — scan this QR code
                      </div>
                      <div className="flex justify-center py-2">
                        <div className="p-2 bg-white border rounded" data-testid="mfa-qr-code"><QRCodeSVG value={mfaSetupData.qr_uri} size={176} /></div>
                      </div>
                      <p className="text-[11px] text-slate-500 text-center">Scan with Google Authenticator, Authy, or any TOTP app.</p>
                      <div className="flex items-center gap-2 bg-white border rounded-md px-3 py-2">
                        <code className="text-xs font-mono text-slate-600 flex-1 select-all" data-testid="mfa-secret">{mfaSetupData.secret}</code>
                        <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => { navigator.clipboard.writeText(mfaSetupData.secret); toast.success("Secret copied"); }}><Copy size={14} /></button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-[#475569] uppercase tracking-wide">Verification Code</Label>
                    <Input type="text" inputMode="numeric" maxLength={6} value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="rounded-md text-center text-2xl tracking-[0.5em] font-mono h-14" autoFocus data-testid="mfa-code-input" />
                  </div>
                  <Button type="submit" disabled={loading || mfaCode.length < 6} className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium h-11 rounded-md flex items-center justify-center gap-2 text-sm" data-testid="mfa-verify-btn">
                    <Shield size={16} />{loading ? "Verifying..." : "Verify & Sign In"}
                  </Button>
                  <button type="button" className="w-full text-xs text-slate-400 hover:text-slate-600 mt-2" onClick={() => { setMfaStep(false); setMfaCode(""); setMfaSetupData(null); setMfaToken(""); setMfaEnforced(false); }}>Back to login</button>
                </form>
              ) : forgotMode ? (
                forgotSent ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><Shield size={24} className="text-emerald-600" /></div>
                    <p className="text-sm text-slate-700 font-medium mb-1">Check your email</p>
                    <p className="text-xs text-slate-500 mb-5">If an account exists for <strong>{forgotEmail}</strong>, we&apos;ve sent a password reset link. The link expires in 1 hour.</p>
                    <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(""); }} className="text-sm text-cyan-600 hover:underline flex items-center gap-1 mx-auto"><ArrowLeft size={14} /> Back to login</button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="forgot-email" className="text-xs font-medium text-[#475569] uppercase tracking-wide">Email Address</Label>
                      <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md md:text-sm" placeholder="you@company.com" autoFocus data-testid="forgot-email-input" />
                    </div>
                    <Button type="submit" disabled={loading || !forgotEmail.trim()} className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium h-11 rounded-md flex items-center justify-center gap-2 text-sm" data-testid="forgot-submit-btn">
                      {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : "Send Reset Link"}
                    </Button>
                    <button type="button" onClick={() => { setForgotMode(false); setForgotEmail(""); }} className="w-full text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 justify-center"><ArrowLeft size={12} /> Back to login</button>
                  </form>
                )
              ) : (
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-medium text-[#475569] uppercase tracking-wide">Email Address</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md md:text-sm" placeholder="you@company.com" data-testid="email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs font-medium text-[#475569] uppercase tracking-wide">Password</Label>
                    <div className="relative">
                      <Input id="password" name="password" type={showPassword ? "text" : "password"} value={formData.password} onChange={handleChange} required className="h-11 bg-white border-[#e2e8f0] focus:border-[#0f172a] focus:ring-1 focus:ring-[#0f172a]/20 rounded-md pr-10 md:text-sm" placeholder="••••••••" data-testid="password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors">
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium h-11 rounded-md flex items-center justify-center gap-2 text-sm transition-colors" data-testid="submit-btn">
                    {loading ? <div className="spinner w-5 h-5 border-white border-t-transparent" /> : <>Sign In <ArrowRight size={16} /></>}
                  </Button>
                </form>
              )}

              {!mfaStep && !forgotMode && (
                <div className="mt-5 text-center">
                  <button type="button" onClick={() => setForgotMode(true)} className="text-sm text-[#64748b] hover:text-[#0f172a] transition-colors" data-testid="forgot-password-link">Forgot your password?</button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 text-center space-y-2">
          <p className="text-[#94a3b8] text-xs">Trusted by procurement teams worldwide</p>
        </div>
      </div>
    </div>
  );
}
